import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Search, Copy, FileDown, ChevronLeft, ChevronRight, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { TableColumnVisibilityMenu, appendSelectedColumnsParam } from '@/components/shared/TableColumnVisibilityMenu'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { financeService } from '@/services/financeService'
import { reportService, AgedReceivablesReportResponse } from '@/services/reportService'
import { FinanceCustomer } from '@/api/types'
import { calendarToday, formatCalendarDate } from '@/lib/calendarDate'
import { formatCurrency } from '@/lib/format'

export const AgedReceivablesReport: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const { user } = useAuthStore()

  const [reportData, setReportData] = useState<AgedReceivablesReportResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [agingDate, setAgingDate] = useState<string>('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)
  
  // Get branch ID from user's selected branch
  const selectedBranchId = user?.selectedBranch?.branchId

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    invoiceNo: true,
    customer: true,
    date: true,
    dueDate: true,
    invoiceAmount: true,
    outstandingAmount: true,
    days_0_30: true,
    days_31_60: true,
    days_61_90: true,
    days_91_180: true,
    days_181_360: true,
    days_360_Plus: true,
    total: true,
  })

  // Load customers when branch is available
  useEffect(() => {
    if (!selectedBranchId) {
      setCustomers([])
      setSelectedCustomerId(undefined)
      return
    }

    const loadCustomers = async () => {
      try {
        const customerList = await financeService.getAllCustomers(selectedBranchId)
        setCustomers(customerList || [])
        // Reset customer selection to "All" when branch changes
        setSelectedCustomerId(undefined)
      } catch (error: any) {
        setCustomers([])
      }
    }
    loadCustomers()
  }, [selectedBranchId])

  // Set default aging date (today)
  useEffect(() => {
    setAgingDate(calendarToday())
  }, [])

  const formatDate = (dateString?: string) => formatCalendarDate(dateString)

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return formatCurrency(Number(amount))
  }

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }

    if (!agingDate) {
      toast.error('Please select an aging date')
      return
    }

    try {
      setLoading(true)
      // Convert date to ISO date-time format (YYYY-MM-DDTHH:mm:ss) - without Z for backend compatibility
      const agingDateTime = `${agingDate}T00:00:00`
      
      // If customer is not selected (All), send customerId as 0
      const customerId = selectedCustomerId !== undefined ? selectedCustomerId : 0
      
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) {
        setPageNumber(1)
      }
      
      const data = await reportService.getAgedReceivablesReport({
        agingDate: agingDateTime,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize: pageSize,
        customerId: customerId,
      })
      setReportData(data)
      setTotalCount(data.totalRecords || data.items?.length || 0)
      const pages = data.totalRecords ? Math.ceil(data.totalRecords / pageSize) : (data.items?.length ? Math.ceil(data.items.length / pageSize) : 0)
      setTotalPages(pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load aged receivables report')
      setReportData(null)
      setTotalCount(0)
      setTotalPages(0)
    } finally {
      setLoading(false)
    }
  }

  // Handle page size change
  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
    // Trigger search with new page size
    if (selectedBranchId && agingDate) {
      setTimeout(() => handleSearch(true), 0)
    }
  }

  // Handle page change
  useEffect(() => {
    if (selectedBranchId && agingDate && pageNumber > 1 && reportData?.items && reportData.items.length > 0) {
      handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const filteredData = useMemo(() => {
    if (!reportData?.items) return []
    if (!searchQuery.trim()) return reportData.items
    const query = searchQuery.toLowerCase()
    return reportData.items.filter((item) => {
      return (
        (item.invoiceNo || '').toLowerCase().includes(query) ||
        (item.customerName || '').toLowerCase().includes(query)
      )
    })
  }, [reportData, searchQuery])

  const handleCustomerChange = (value: string) => {
    // When "All" is selected, set to undefined so we can send 0 to API
    setSelectedCustomerId(value === 'all' ? undefined : Number(value))
  }

  const handleCopy = () => {
    if (!reportData || filteredData.length === 0) {
      toast.error('No data to copy')
      return
    }

    // Create CSV-like text
    const headers = [
      'Invoice No',
      'Customer',
      'Date',
      'Due Date',
      'Invoice Amount',
      'Balance Amount',
      '0-30 Days',
      '31-60 Days',
      '61-90 Days',
      '91-180 Days',
      '181-360 Days',
      '360 Above Days',
      'Total',
    ].join('\t')

    const rows = filteredData.map(item => [
      item.invoiceNo,
      item.customerName,
      item.invoiceDate,
      item.dueDate,
      formatAmount(item.total),
      formatAmount(item.outstandingAmount),
      formatAmount(item.days_0_30),
      formatAmount(item.days_31_60),
      formatAmount(item.days_61_90),
      formatAmount(item.days_91_180),
      formatAmount(item.days_181_360),
      formatAmount(item.days_360_Plus),
      formatAmount(item.total),
    ].join('\t'))

    const csvContent = [headers, ...rows].join('\n')

    navigator.clipboard.writeText(csvContent)
      .then(() => toast.success('Data copied to clipboard'))
      .catch(() => toast.error('Failed to copy data'))
  }

  const handleCsv = () => {
    if (!reportData || filteredData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Create CSV content
    const headers = [
      'Invoice No',
      'Customer',
      'Date',
      'Due Date',
      'Invoice Amount',
      'Balance Amount',
      '0-30 Days',
      '31-60 Days',
      '61-90 Days',
      '91-180 Days',
      '181-360 Days',
      '360 Above Days',
      'Total',
    ].join(',')

    const rows = filteredData.map(item => [
      `"${item.invoiceNo}"`,
      `"${item.customerName}"`,
      `"${item.invoiceDate}"`,
      `"${item.dueDate}"`,
      formatAmount(item.total),
      formatAmount(item.outstandingAmount),
      formatAmount(item.days_0_30),
      formatAmount(item.days_31_60),
      formatAmount(item.days_61_90),
      formatAmount(item.days_91_180),
      formatAmount(item.days_181_360),
      formatAmount(item.days_360_Plus),
      formatAmount(item.total),
    ].join(','))

    const csvContent = [headers, ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `aged-receivables-${agingDate || 'report'}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  const handlePrint = () => {
    if (!selectedBranchId || !agingDate) {
      toast.error('Please select branch and aging date before printing')
      return
    }
    
    // Navigate to print page with query parameters
    const params = new URLSearchParams({
      agingDate,
      branchId: String(selectedBranchId),
    })
    if (selectedCustomerId) {
      params.append('customerId', String(selectedCustomerId))
    }
    appendSelectedColumnsParam(params, visibleColumns)
    
    window.open(`/reports/aged-receivables/print?${params.toString()}`, '_blank')
  }

  const toggleColumn = (columnKey: string) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }))
  }

  const agedReceivablesColumnMenu = useMemo(
    () => [
      { id: 'invoiceNo', label: t('reports.invoiceNo') },
      { id: 'customer', label: t('reports.customer') },
      { id: 'date', label: t('reports.date') },
      { id: 'dueDate', label: t('reports.dueDate') },
      { id: 'invoiceAmount', label: t('reports.invoiceAmount') },
      { id: 'outstandingAmount', label: t('reports.outstandingAmount') },
      { id: 'days_0_30', label: t('reports.days_0_30') },
      { id: 'days_31_60', label: t('reports.days_31_60') },
      { id: 'days_61_90', label: t('reports.days_61_90') },
      { id: 'days_91_180', label: t('reports.days_91_180') },
      { id: 'days_181_360', label: t('reports.days_181_360') },
      { id: 'days_360_Plus', label: t('reports.days_360_Plus') },
      { id: 'total', label: t('reports.total') },
    ],
    [t],
  )

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.agedReceivablesReport')}
        description={t('reports.viewAgedReceivables') || 'View aged receivables report by invoice'}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.agingDate')}</Label>
              <Input
                type="date"
                value={agingDate}
                onChange={(e) => setAgingDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.customer')}</Label>
              <Select
                value={selectedCustomerId ? String(selectedCustomerId) : 'all'}
                onValueChange={handleCustomerChange}
                disabled={!selectedBranchId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('reports.allCustomers')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('reports.allCustomers')}</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.customerId} value={String(customer.customerId)}>
                      {customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleSearch(true)} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <TableColumnVisibilityMenu
                columns={agedReceivablesColumnMenu}
                visibility={visibleColumns}
                onToggle={toggleColumn}
              />
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                {t('reports.print')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : !reportData || filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load aged receivables report"
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                      {visibleColumns.invoiceNo && (
                        <TableHead className={`font-bold text-primary  px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.invoiceNo')}</TableHead>
                      )}
                      {visibleColumns.customer && (
                        <TableHead className={`font-bold text-primary  px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.customer')}</TableHead>
                      )}
                      {visibleColumns.date && (
                        <TableHead className={`font-bold text-primary  px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.date')}</TableHead>
                      )}
                      {visibleColumns.dueDate && (
                        <TableHead className={`font-bold text-primary  px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.dueDate')}</TableHead>
                      )}
                      {visibleColumns.invoiceAmount && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.invoiceAmount')}</TableHead>
                      )}
                      {visibleColumns.outstandingAmount && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.outstandingAmount')}</TableHead>
                      )}
                      {visibleColumns.days_0_30 && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.days_0_30')}</TableHead>
                      )}
                      {visibleColumns.days_31_60 && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.days_31_60')}</TableHead>
                      )}
                      {visibleColumns.days_61_90 && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.days_61_90')}</TableHead>
                      )}
                      {visibleColumns.days_91_180 && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.days_91_180')}</TableHead>
                      )}
                      {visibleColumns.days_181_360 && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.days_181_360')}</TableHead>
                      )}
                      {visibleColumns.days_360_Plus && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.days_360_Plus')}</TableHead>
                      )}
                      {visibleColumns.total && (
                        <TableHead className="font-bold text-primary text-right  px-2 py-2">{t('reports.total')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((item, index) => (
                      <TableRow
                        key={index}
                        className="border-b hover:bg-primary/5 transition-colors"
                      >
                        {visibleColumns.invoiceNo && (
                          <TableCell className=" px-2 py-2">{item.invoiceNo}</TableCell>
                        )}
                        {visibleColumns.customer && (
                          <TableCell className=" px-2 py-2">{item.customerName}</TableCell>
                        )}
                        {visibleColumns.date && (
                          <TableCell className=" px-2 py-2">{formatDate(item.invoiceDate)}</TableCell>
                        )}
                        {visibleColumns.dueDate && (
                          <TableCell className=" px-2 py-2">{formatDate(item.dueDate)}</TableCell>
                        )}
                        {visibleColumns.invoiceAmount && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.total)}</TableCell>
                        )}
                        {visibleColumns.outstandingAmount && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.outstandingAmount)}</TableCell>
                        )}
                        {visibleColumns.days_0_30 && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.days_0_30)}</TableCell>
                        )}
                        {visibleColumns.days_31_60 && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.days_31_60)}</TableCell>
                        )}
                        {visibleColumns.days_61_90 && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.days_61_90)}</TableCell>
                        )}
                        {visibleColumns.days_91_180 && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.days_91_180)}</TableCell>
                        )}
                        {visibleColumns.days_181_360 && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.days_181_360)}</TableCell>
                        )}
                        {visibleColumns.days_360_Plus && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.days_360_Plus)}</TableCell>
                        )}
                        {visibleColumns.total && (
                          <TableCell className="text-right  px-2 py-2">{formatAmount(item.total)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    {reportData && (() => {
                      const visibleCount = Object.values(visibleColumns).filter(v => v).length
                      const amountColumnsCount = [
                        visibleColumns.invoiceAmount,
                        visibleColumns.outstandingAmount,
                        visibleColumns.days_0_30,
                        visibleColumns.days_31_60,
                        visibleColumns.days_61_90,
                        visibleColumns.days_91_180,
                        visibleColumns.days_181_360,
                        visibleColumns.days_360_Plus,
                        visibleColumns.total,
                      ].filter(v => v).length
                      const textColumnsCount = visibleCount - amountColumnsCount
                      
                      return (
                        <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                          <TableCell colSpan={textColumnsCount} className="text-right  px-2 py-2">
                            Total
                          </TableCell>
                          {visibleColumns.invoiceAmount && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.totalAmount)}</TableCell>
                          )}
                          {visibleColumns.outstandingAmount && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.totalOutstanding)}</TableCell>
                          )}
                          {visibleColumns.days_0_30 && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.total_0_30)}</TableCell>
                          )}
                          {visibleColumns.days_31_60 && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.total_31_60)}</TableCell>
                          )}
                          {visibleColumns.days_61_90 && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.total_61_90)}</TableCell>
                          )}
                          {visibleColumns.days_91_180 && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.total_91_180)}</TableCell>
                          )}
                          {visibleColumns.days_181_360 && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.total_181_360)}</TableCell>
                          )}
                          {visibleColumns.days_360_Plus && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.total_360_Plus)}</TableCell>
                          )}
                          {visibleColumns.total && (
                            <TableCell className="text-right  px-2 py-2">{formatAmount(reportData.totalAmount)}</TableCell>
                          )}
                        </TableRow>
                      )
                    })()}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

